import { useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { CartProvider } from './contexts/CartContext';
import { WishlistProvider } from './contexts/WishlistContext';
import { NotificationProvider } from './contexts/NotificationContext';
import NotificationBanner from './components/NotificationBanner';
import Navbar from './components/shared/Navbar';
import { Footer } from './components/shared/index';
import HomePage from './pages/HomePage';
import BrowsePage from './pages/BrowsePage';
import ProductDetailPage from './pages/ProductDetailPage';
import CartPage from './pages/CartPage';
import { WishlistPage } from './pages/UserPages';
import { OrdersPage } from './pages/OrdersPage';
import { LoginPage, RegisterPage, ForgotPasswordPage, ResetPasswordPage } from './pages/AuthPages';
import VerifyEmailPage from './pages/VerifyEmailPage';
import IdentityVerificationPage from './pages/IdentityVerificationPage';
import ProfilePage from './pages/ProfilePage';
import PaymentSuccessPage from './pages/PaymentSuccessPage';
import { AdminPanel } from './pages/AdminPages';
import TrustScorePage from './pages/TrustScorePage';
import WarbadobeChatbot from './components/WarbadobeChatbot';
import './styles/global.css';

function PageSpinner() {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '80vh' }}>
      <div style={{
        width: 40, height: 40,
        border: '3px solid var(--border)',
        borderTopColor: 'var(--gold)',
        borderRadius: '50%',
        animation: 'spin 0.7s linear infinite'
      }} />
    </div>
  );
}

function PrivateRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <PageSpinner />;
  return user ? children : <Navigate to="/login" replace />;
}

function AdminRoute({ children }) {
  const { user, loading, isAdmin } = useAuth();
  if (loading) return <PageSpinner />;
  if (!user) return <Navigate to="/login" replace />;
  if (!isAdmin) return <Navigate to="/" replace />;
  return children;
}

function GuestRoute({ children }) {
  const { user, loading, isAdmin } = useAuth();
  if (loading) return null;
  if (user) return <Navigate to={isAdmin ? '/admin' : '/browse'} replace />;
  return children;
}


function AppContent() {
  const { isAdmin } = useAuth();
  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <Navbar />
      {/* Ask user to enable push notifications (shows only when Firebase is configured + permission not yet decided) */}
      <NotificationBanner />
      <div style={{ flex: 1 }}>
        <Routes>
          {/* ── Public ────────────────────────────────────────────────── */}
          <Route path="/"           element={<HomePage />} />
          <Route path="/browse"     element={<BrowsePage />} />
          <Route path="/product/:id" element={<ProductDetailPage />} />

          {/* ── Auth: guest only ──────────────────────────────────────── */}
          <Route path="/login"           element={<GuestRoute><LoginPage /></GuestRoute>} />
          <Route path="/register"        element={<GuestRoute><RegisterPage /></GuestRoute>} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password"  element={<ResetPasswordPage />} />

          {/* ── Email verification: public (accessed from email link) ── */}
          <Route path="/verify-email" element={<VerifyEmailPage />} />

          {/* ── Protected user routes ─────────────────────────────────── */}
          <Route path="/profile"         element={<PrivateRoute><ProfilePage /></PrivateRoute>} />
          <Route path="/cart"            element={<PrivateRoute><CartPage /></PrivateRoute>} />
          <Route path="/wishlist"        element={<PrivateRoute><WishlistPage /></PrivateRoute>} />
          <Route path="/orders"          element={<PrivateRoute><OrdersPage /></PrivateRoute>} />
          <Route path="/orders/:id"      element={<PrivateRoute><OrdersPage /></PrivateRoute>} />
          <Route path="/trust"           element={<PrivateRoute><TrustScorePage /></PrivateRoute>} />
          <Route path="/verify-identity" element={<PrivateRoute><IdentityVerificationPage /></PrivateRoute>} />
          <Route path="/payment/success" element={<PrivateRoute><PaymentSuccessPage /></PrivateRoute>} />

          {/* ── Admin ─────────────────────────────────────────────────── */}
          <Route path="/admin/*" element={<AdminRoute><AdminPanel /></AdminRoute>} />

          {/* ── Catch-all ─────────────────────────────────────────────── */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </div>
      {!isAdmin && <Footer />}
      {!isAdmin && <WarbadobeChatbot />}
    </div>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <NotificationProvider>
          <CartProvider>
            <WishlistProvider>
              <AppContent />
              <Toaster
                position="bottom-right"
                toastOptions={{
                  style: {
                    fontFamily: "'Josefin Sans', sans-serif",
                    fontSize: 12,
                    letterSpacing: '0.04em',
                    background: '#0e0b08',
                    color: '#f5f0e8',
                    borderRadius: 2,
                    border: '1px solid rgba(184,146,74,0.3)'
                  },
                  success: { iconTheme: { primary: '#b8924a', secondary: '#0e0b08' } },
                  error:   { iconTheme: { primary: '#c0392b', secondary: 'white'   } },
                  duration: 4000
                }}
              />
            </WishlistProvider>
          </CartProvider>
        </NotificationProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}